/**
 * Lazy loads images that are set up properly. Aslo handled webp fallbacks
 * For img tags:
 *      <picture>
            <source class="img-load-lazy" data-srcset="/img.webp" type="image/webp" />
            <source class="img-load-lazy" data-srcset="/img.jpg" type="image/jpeg" />
            <img class="img-load-lazy" data-src="/img.jpg" alt="Image Alt" />
          </picture>

    For BG Images: <div class="lazy-load-bg" style="background-image: url('/my-image.jpeg')"></div>

    For BG Images with webp: <div class="lazy-load-bg" style="background-image: url('/my-image.jpeg')" data-webp="/path-to-img.webp"></div>

    more info: https://developers.google.com/web/fundamentals/performance/lazy-loading-guidance/images-and-video
 */

export class ImageLoader {
    public queue;
    public backgroundQueue;
    public lazyImageObserver;
    public lazyBgObserver;
    public options;
    public webpSupported;

    constructor(options = {}) {
        this.queue = [];
        this.backgroundQueue = [];
        this.lazyImageObserver = null;
        this.lazyBgObserver = null;
        this.options = options;
        this.webpSupported = ImageLoader.supportsWebp();
    }

    public initialize() {
        this.queue = [];
        this.backgroundQueue = [];
        this.lazyImageObserver = null;
        this.lazyBgObserver = null;

        this.getImagesToLazyLoad();

        if (!("IntersectionObserver" in window)) {
            this.abort();
            return;
        }

        this.onContentLoaded();
        this.setGenericPlaceholder();

        this.queue.forEach((lazyImage) => {
            this.lazyImageObserver.observe(lazyImage);
        });

        this.backgroundQueue.forEach((lazyBg) => {
            this.lazyBgObserver.observe(lazyBg);
        });
    }

    getImagesToLazyLoad() {
        this.queue = document.querySelectorAll('.img-load-lazy');
        this.backgroundQueue = document.querySelectorAll('.lazy-load-bg');
    }

    setGenericPlaceholder() {
        if (this.options.genericImagePlaceholder) {
            this.queue.forEach((img) => {
                img.setAttribute('src', this.options.genericImagePlaceholder);
            });
        }

        if (this.options.genericBgPlaceholder) {
            this.backgroundQueue.forEach((bg) => {
                bg.style.backgroundColor = this.options.genericBgPlaceholder;
            });
        }
    }

    onContentLoaded() {
        this.lazyImageObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    let lazyImage: any = entry.target;

                    if (lazyImage.classList.contains('img-load-lazy')) {
                        if (lazyImage.dataset.src) {
                            lazyImage.src = lazyImage.dataset.src;
                        }
                        if (lazyImage.dataset.srcset) {
                            lazyImage.srcset = lazyImage.dataset.srcset;
                        }
                        lazyImage.classList.remove('img-load-lazy');
                        this.lazyImageObserver.unobserve(lazyImage);
                    }
                }
            });
        });

        this.lazyBgObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && entry.target.classList.contains('lazy-load-bg')) {
                    const webP = entry.target.getAttribute('data-webp');
                    if (this.webpSupported && webP && webP != '') {
                        entry.target.setAttribute('style', `background-image: url(${webP})`);
                    }

                    entry.target.classList.remove('lazy-load-bg');
                    this.lazyBgObserver.unobserve(entry.target);
                }
            });
        });
    }

    abort() {
        for (let i = 0; i < this.queue.length; i++) {
            this.queue[i].setAttribute('src', this.queue[i].getAttribute('data-src'));
            this.queue[i].classList.remove('img-load-lazy');
        }

        for (let i = 0; i < this.backgroundQueue.length; i++) {
            this.backgroundQueue[i].classList.remove('lazy-load-bg');
        }
    }

    static supportsWebp() {
        var elem = document.createElement('canvas');

        if (!!(elem.getContext && elem.getContext('2d'))) {
            // was able or not to get WebP representation
            return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
        }
        else {
            // very old browser like IE 8, canvas not supported
            return false;
        }
    }

}

interface AspectRatioInterface {
    ratio: Array<number>,
    element: HTMLElement,
    type?: string;
};

export class AspectRatio {

    public ratioX: number;
    public ratioY: number;
    public element: HTMLElement;
    public type: string;

    constructor(options: AspectRatioInterface) {
        this.ratioX = options.ratio[0];
        this.ratioY = options.ratio[1];
        this.element = options.element;
        this.type = options.type;
    }

    maintain() {
        this.setAspectRatio();

        window.addEventListener('resize', () => {
            this.setAspectRatio();
        });
    }

    setAspectRatio() {
        const currentWidth = this.element.clientWidth;
        const heightPctByRatio = this.ratioY / this.ratioX;
        const computedHeight = currentWidth * heightPctByRatio;

        if (this.type && this.type == 'min-height') {
            this.element.style.minHeight = computedHeight + 'px';
        } else {
            this.element.style.height = computedHeight + 'px';
        }
    }

}
